'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { Fragment, useCallback } from 'react';
import styled from 'styled-components';
import { Set as ImmutableSet } from 'immutable';
import I18n from 'I18n';
import { FormattedMessage } from 'LearningCenterUI/components/I18n';
import LanguageNames from 'ui-addon-i18n/constants/LanguageNames';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import UIDropdown from 'UIComponents/dropdown/UIDropdown';
import UICheckbox from 'UIComponents/input/UICheckbox';
import UISearchInput from 'UIComponents/input/UISearchInput';
import UISelect from 'UIComponents/input/UISelect';
import UITag from 'UIComponents/tag/UITag';
import { AWARD_TYPES, CATEGORIES, CONTENT_TYPES, DURATIONS, LANGUAGES, LEVELS, SORT_BYS, ASSIGNED_BY, TOOLS, CREATED_BY } from '../../constants/TopFilterConstants';
import { useSearchParam, useSearchParamInput, useDebouncedTextInput, useLocationWithoutParams } from '../../hooks/searchParamHooks';
import { device } from '../../constants/StyledComponentConstants';
import useMediaQuery from '../../hooks/useMediaQuery';
import UsageTracker from '../../utils/UsageTracker';
import ImmutablePropTypes from 'react-immutable-proptypes'; // Link each filter query key with possible values from consts

export const items = {
  awardType: AWARD_TYPES,
  cat: CATEGORIES,
  contentType: CONTENT_TYPES,
  createdBy: CREATED_BY,
  duration: DURATIONS,
  language: LANGUAGES,
  level: LEVELS,
  sortBy: SORT_BYS,
  tools: TOOLS,
  assignedBy: ASSIGNED_BY
};
const ClearFiltersRouterLink = styled(UIRouterLink).withConfig({
  displayName: "RouterDropdownFilter__ClearFiltersRouterLink",
  componentId: "sc-1tso3s7-0"
})(["white-space:nowrap;padding-left:10px;top:3px;position:relative;"]);
export const StyledTagsContainer = styled.div.withConfig({
  displayName: "RouterDropdownFilter__StyledTagsContainer",
  componentId: "sc-1tso3s7-1"
})(["line-height:2.4;margin:-10px 0 0 -4px;padding-bottom:10px;display:flex;flex-wrap:wrap;@media screen and ", "{flex-wrap:nowrap;overflow-x:scroll;margin:0;padding-left:8px;padding-top:5px;padding-bottom:6px;&:empty{padding:0;}a:last-child{padding-right:12px;}&::-webkit-scrollbar{width:0;background:transparent;height:0;}}"], device.mobile);
const StyledUISelect = styled(UISelect).withConfig({
  displayName: "RouterDropdownFilter__StyledUISelect",
  componentId: "sc-1tso3s7-2"
})(["& [role='option']{font-weight:500;}"]);

const onClearFiltersClick = () => {
  UsageTracker.track('interaction', {
    action: 'clear-filters-click'
  });
};

export const ClearFiltersLink = ({
  filtersToClear
}) => {
  const [isClearable, destination] = useLocationWithoutParams(filtersToClear);
  return isClearable ? /*#__PURE__*/_jsx(ClearFiltersRouterLink, {
    "data-test-id": "clear-top-filters",
    onClick: onClearFiltersClick,
    to: destination,
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.filters.clearFilters"
    })
  }) : null;
};
export const RouterChecklistFilter = ({
  defaultItem,
  filterType,
  replace,
  hideReset
}) => {
  const options = items[filterType] || [];
  const [paramValue, changeHandler] = useSearchParamInput(filterType, replace);
  const [__, changeHandlerReplace] = useSearchParamInput(filterType, true);
  let valuesSet = ImmutableSet(paramValue);

  if (!valuesSet.size && defaultItem) {
    valuesSet = valuesSet.add(defaultItem);
  }

  return [!hideReset && /*#__PURE__*/_jsx(UICheckbox, {
    checked: valuesSet.isEmpty(),
    "data-test-id": `${filterType}-filterReset`,
    onChange: changeHandlerReplace,
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `learningCenter.topFilters.${filterType}.filterReset`
    })
  }, "filterReset"), options.map(item => /*#__PURE__*/_jsx(UICheckbox, {
    alignment: "end",
    checked: valuesSet.contains(item),
    "data-test-id": `${filterType}-${item}`,
    onChange: changeHandler,
    value: item,
    children: filterType === 'language' ? LanguageNames[item.toLowerCase()] : /*#__PURE__*/_jsx(FormattedMessage, {
      message: `learningCenter.topFilters.${filterType}.${item}`
    })
  }, item))];
};
RouterChecklistFilter.propTypes = {
  defaultItem: PropTypes.string,
  filterType: PropTypes.string.isRequired,
  hideReset: PropTypes.bool,
  replace: PropTypes.bool
};
const DropdownButtonText = styled(FormattedMessage).withConfig({
  displayName: "RouterDropdownFilter__DropdownButtonText",
  componentId: "sc-1tso3s7-3"
})(["font-weight:500;"]);

const RouterDropdownFilter = props => {
  const {
    filterType
  } = props;
  return /*#__PURE__*/_jsx(UIDropdown, {
    buttonText: /*#__PURE__*/_jsx(DropdownButtonText, {
      message: `learningCenter.topFilters.${filterType}.filterTitle`
    }),
    buttonUse: "transparent",
    "data-test-id": `filter-type-${filterType}`,
    children: /*#__PURE__*/_jsx(RouterChecklistFilter, Object.assign({}, props))
  });
};

RouterDropdownFilter.propTypes = {
  filterType: PropTypes.string.isRequired,
  replace: PropTypes.bool
};
export const RouterSelectSingle = (_ref) => {
  let {
    allowList,
    defaultItemIndex,
    defaultItem,
    filterType,
    trackingKey = filterType,
    filterClearingValue,
    retainSelection
  } = _ref,
      props = _objectWithoutPropertiesLoose(_ref, ["allowList", "defaultItemIndex", "defaultItem", "filterType", "trackingKey", "filterClearingValue", "retainSelection"]);

  const options = items[filterType] || [];
  const [paramValue, setSearchParamInput] = useSearchParamInput(filterType, true);
  const defaultValue = defaultItem || options[defaultItemIndex];
  const selected = paramValue[0] || defaultValue;
  const onOpen = useCallback(({
    currentTarget
  }) => {
    const {
      value
    } = currentTarget;

    if (value) {
      UsageTracker.track('filterInteraction', {
        action: 'filter-menu-opened',
        key: trackingKey,
        value: selected
      });
    }
  }, [selected, trackingKey]);
  const changeHandler = useCallback(changeEvent => {
    const {
      currentTarget: {
        value
      }
    } = changeEvent;

    if (filterClearingValue && filterClearingValue === value.toLowerCase()) {
      setSearchParamInput({
        target: {
          value: ''
        }
      });
    } else {
      setSearchParamInput(changeEvent);
    }

    UsageTracker.track('filterInteraction', {
      action: 'filter-selected',
      value,
      key: trackingKey,
      defaultValue
    });
  }, [filterClearingValue, trackingKey, defaultValue, setSearchParamInput]);
  return /*#__PURE__*/_jsx(StyledUISelect, Object.assign({
    buttonUse: "transparent",
    "data-test-id": `filter-type-${filterType}`,
    onChange: changeHandler,
    onOpenChange: onOpen,
    options: options.map(option => ({
      disabled: !!allowList && !allowList.map(allowItem => allowItem.toLowerCase()).includes(option.toLowerCase()),
      text: filterType === 'language' ? LanguageNames[option.toLowerCase()] : I18n.text(`learningCenter.topFilters.${filterType}.${option}`),
      value: option
    })),
    value: retainSelection ? selected : null
  }, props));
};
RouterSelectSingle.defaultProps = {
  defaultItemIndex: 0,
  retainSelection: true
};
RouterSelectSingle.propTypes = {
  allowList: ImmutablePropTypes.listOf(PropTypes.string),
  defaultItem: PropTypes.string,
  defaultItemIndex: PropTypes.number.isRequired,
  filterClearingValue: PropTypes.string,
  filterType: PropTypes.string.isRequired,
  retainSelection: PropTypes.bool,
  trackingKey: PropTypes.string
};
export const RouterSearchInput = (_ref2) => {
  let {
    filterType,
    clearCatalogFilters,
    searchResultsRedirect,
    size
  } = _ref2,
      rest = _objectWithoutPropertiesLoose(_ref2, ["filterType", "clearCatalogFilters", "searchResultsRedirect", "size"]);

  const [inputValue, onChange] = useDebouncedTextInput(filterType, 300, clearCatalogFilters, searchResultsRedirect);
  return /*#__PURE__*/_jsx(UISearchInput, Object.assign({
    "data-test-id": `filter-type-${filterType}`,
    onChange: onChange,
    placeholder: I18n.text(`learningCenter.topFilters.${filterType}.placeholder`),
    size: size,
    value: inputValue
  }, rest));
};
RouterSearchInput.propTypes = {
  clearCatalogFilters: PropTypes.bool.isRequired,
  filterType: PropTypes.string.isRequired,
  searchResultsRedirect: PropTypes.bool,
  size: PropTypes.string
};
RouterSearchInput.defaultProps = {
  clearCatalogFilters: false
};
export const RouterFilterTags = ({
  filterType
}) => {
  const [paramValue, setParam] = useSearchParam(filterType);
  const valueSet = ImmutableSet(paramValue);
  const options = items[filterType] || [];
  const isMobile = useMediaQuery(`screen and ${device.mobile}`);
  return /*#__PURE__*/_jsx(Fragment, {
    children: options.map(item => valueSet.has(item) && /*#__PURE__*/_jsx("div", {
      onClick: () => {
        setParam(valueSet.delete(item).toArray());
        UsageTracker.track('filterInteraction', {
          action: 'filter-deselected',
          value: item,
          key: filterType,
          isTag: true
        });
      },
      children: /*#__PURE__*/_jsxs(UITag, {
        closeable: true,
        "data-test-id": `filter-tag-${filterType}-${item}`,
        inline: true,
        children: [!isMobile && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: `learningCenter.topFilters.${filterType}.filterTitle`
          }), ":", ' ']
        }), filterType === 'language' ? LanguageNames[item.toLowerCase()] : /*#__PURE__*/_jsx(FormattedMessage, {
          message: `learningCenter.topFilters.${filterType}.${item}`
        })]
      }, `${filterType}-${item}`)
    }, item))
  });
};
RouterFilterTags.propTypes = {
  filterType: PropTypes.string.isRequired
};
export default RouterDropdownFilter;